import {
  AppBar,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Toolbar,
  Typography,
} from '@mui/material';
import { Fragment, type ReactNode } from 'react';

import { spacing } from '../theme/tokens.js';

export interface NavItem {
  label: string;
  href: string;
  active?: boolean;
  /** External links (e.g. /kanban.html) skip the renderLink override and use a plain <a>. */
  external?: boolean;
  /**
   * Optional grouping cluster. When the section changes between two
   * consecutive items, a subheader is rendered above the item. Items with no
   * section render ungrouped (typically at the top). Backward-compatible:
   * existing callers that pass no section get a flat, un-subheadered list.
   */
  section?: string;
}

export interface AppShellProps {
  title: string;
  nav: NavItem[];
  /** Render-prop for nav item link element (e.g. TanStack Router's `Link`). */
  renderLink?: (item: NavItem, children: ReactNode) => ReactNode;
  /** Optional right-aligned content in the AppBar (user menu, etc.). */
  appBarRight?: ReactNode;
  children: ReactNode;
}

const DRAWER_WIDTH = 240;

/**
 * Page shell: persistent left drawer + top AppBar + main content.
 * Designed to be router-agnostic — the parent passes a `renderLink`
 * that wires nav items to whatever routing library is in use.
 */
export function AppShell({
  title,
  nav,
  renderLink,
  appBarRight,
  children,
}: AppShellProps) {
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        color="default"
        elevation={0}
        sx={{
          zIndex: (t) => t.zIndex.drawer + 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        <Toolbar>
          <Typography variant="h3" component="div" sx={{ flexGrow: 1 }}>
            {title}
          </Typography>
          {appBarRight}
        </Toolbar>
      </AppBar>

      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            borderRight: 1,
            borderColor: 'divider',
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', py: 1 }}>
          <List dense>
            {nav.map((item, index) => {
              const content = (
                <ListItemButton selected={item.active}>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              );
              // Render a section subheader when this item starts a new section
              // (i.e. its section differs from the previous item's). Items with
              // no section never emit a subheader — keeps un-sectioned callers
              // flat and the leading un-sectioned cluster ungrouped.
              const prevSection = index > 0 ? nav[index - 1]?.section : undefined;
              const showSubheader = item.section != null && item.section !== prevSection;
              return (
                <Fragment key={item.href}>
                  {showSubheader ? (
                    <ListSubheader
                      disableSticky
                      sx={{ bgcolor: 'transparent', lineHeight: 2.5, mt: index > 0 ? 1 : 0 }}
                    >
                      {item.section}
                    </ListSubheader>
                  ) : null}
                  <ListItem disablePadding>
                    {item.external ? (
                      <a
                        href={item.href}
                        style={{ textDecoration: 'none', color: 'inherit', width: '100%' }}
                      >
                        {content}
                      </a>
                    ) : renderLink ? (
                      renderLink(item, content)
                    ) : (
                      content
                    )}
                  </ListItem>
                </Fragment>
              );
            })}
          </List>
        </Box>
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: 'background.default',
          p: `${spacing.lg}px`,
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
}
